import React, { useCallback, useMemo } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, useLocation } from "react-router";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Tooltip } from "@progress/kendo-react-tooltip";
import _ from "lodash";
import { icon, label, number, route, tooltip } from "../../../../config";
import { assignedTypes } from "../../../Tasks/tasks.constants";
import { addTaskApprovalsHistory, handleTaskKeyUpdate } from "../../../Tasks/tasks.service";
import { getCurrentActiveProject } from "../../sidebar.common";
import { isTrueBit } from "../../../../utils";
import ApprovalAssignment from "./ApprovalAssignment";
import { approvalActionValidation, approverActionitemRender, approverActionvalueRender, getApprovalCurrentProject, isApprover, resetApprovalsDropdown, resetApprovalsDropdownNew } from "./approval.helper";


/**
 * Approver View component, it is rendered in approval component
 * @returns JSX
 * @author Himanshu Negi
 */
const ApproverView = () => {
  const [approverAction, setApproverAction] = React.useState(null);
  const [approvalAssignType, setApprovalAssignType] = React.useState(null);
  const [approvalAssignId, setApprovalAssignId] = React.useState(null);
  const [approvalRelatedAssignedType, setApprovalRelatedAssignedType] = React.useState(null);
  const [approvalRelatedAssignedId, setApprovalRelatedAssignedId] = React.useState(null);
  const [relatedAssignedIdList, setRelatedAssignedIdList] = React.useState([]);
  const [approvalAssignee, setApprovalAssignee] = React.useState(null);
  const [approvalAssigneeName, setApprovalAssigneeName] = React.useState("");
  const [approvalSelection, setApprovalSelection] = React.useState(["team", "user"]);
  const [note, setNote] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [showNoteInput, setShowNoteInput] = React.useState(false);
  const [disable, setDisable] = React.useState(false);
  const [isTaskLocked, setIsTaskLocked] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [targetElement, setTargetElement] = React.useState(null);
  const wrapper = React.useRef(null);

  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { user } = useSelector((store) => store.auth);
  const taskMatchPath = matchPath(pathname, { path: route.PRIVATE_ROUTE.TASKS.ROUTER_PATH });
  const { isNewTask, task, approvalLogs, flowElements } = useSelector((store) => store.taskSidebar);
  const { defaultDetails } = useSelector((store) => store.tasks);
  const projectWorkflows = useSelector((store) => store.projectWorkflows);
  const getApprovalCurrentProjectArgs = { approvalAssignId, approvalAssignType: approvalAssignId?.AssignmentType ? { key: approvalAssignId?.AssignmentType } : approvalAssignType, task, defaultDetails, isNewTask };
  const approvalActionValidationArgs = {
    approverAction,
    approvalLogs,
    approvalAssignType: approvalAssignId?.AssignmentType ? { key: approvalAssignId?.AssignmentType } : approvalAssignType,
    task,
    approvalAssignId,
    approvalAssignee,
    approvalRelatedAssignedType: approvalRelatedAssignedId?.RelatedAssignmentType ? { value: approvalRelatedAssignedId?.RelatedAssignmentType } : approvalRelatedAssignedType,
    approvalRelatedAssignedId,
  };
  const approvalProjectName = useMemo(
    () => (approvalAssignId?.AssignmentType === number.TWO ? approvalAssignId?.label : approvalAssignId?.ProjectName),
    [approvalAssignId]
  );

  /**
   * field to check if current flow is locked or not
   * @author Himanshu Negi
   */
  const isCurrentFlowLocked = useMemo(() => {
    const reversedFlowElements = _.reverse([...flowElements]);
    const currentProject = reversedFlowElements?.find((flow) => flow?.isCompleted === number.ONE);
    return currentProject ? currentProject.isFlowLocked : number.ZERO;
  }, [flowElements]);

  /**
   * field to check if approval is self approval
   * @author Himanshu Negi
   */
  const isSelfApproval = useMemo(() => {
    const approvalInitiateLog = approvalLogs?.findLast((log) => log.ApprovalStatus === number.ONE);
    return approvalInitiateLog?.IsSelfApproval;
  }, [approvalLogs]);

  /**
   * approver action list
   * @author Himanshu Negi
   */
  const approverActionList = useMemo(() => {
    const isTaskInApproval = isTrueBit(task, "InApproval");
    
    if (isSelfApproval) {
      /* For self-approval, exclude ONE, THREE, FOUR, SEVEN, EIGHT */
      /* Also exclude FIVE (Recommend) if task is in approval */
      const excludedStatusIds = [number.ONE, number.THREE, number.FOUR, number.SEVEN, number.EIGHT];
      if (isTaskInApproval && !excludedStatusIds.includes(number.FIVE)) {
        excludedStatusIds.push(number.FIVE);
      }
      return defaultDetails?.approvalStatus?.filter(
        (action) => !excludedStatusIds.includes(action?.StatusId)
      );
    }
    
    /* For non-self-approval, exclude ONE, SEVEN */
    /* Also exclude FOUR (Reassign) and FIVE (Recommend) if task is in approval */
    const excludedStatusIds = [number.ONE, number.SEVEN];
    if (isTaskInApproval) {
      excludedStatusIds.push(number.FOUR, number.FIVE);
    }
    
    return defaultDetails?.approvalStatus?.filter(
      (action) => !excludedStatusIds.includes(action?.StatusId)
    );
  }, [isSelfApproval, defaultDetails?.approvalStatus, task]);

  /**
   * It enables the approval assignment inputs only if approver chooses reassign action
   * @author Himanshu Negi
   */
  useEffect(() => {
    const disableValue = approverAction?.StatusId === number.FOUR || approverAction?.StatusId === number.FIVE ? false : true;
    setDisable(disableValue);
  }, [approverAction]);

  /**
   * sets initial values of approvalAssignType, approvalAssignId, approvalRelatedAssignedType, approvalRelatedAssignedId,
   * @author Himanshu Negi
   */
  useEffect(() => {
    (async () => {
      const resetPayload = {
        task,
        setApprovalAssignType,
        setApprovalAssignId,
        setApprovalRelatedAssignedType,
        setRelatedAssignedIdList,
        setApprovalRelatedAssignedId,
        setApprovalSelection,
        defaultDetails,
      };
      await resetApprovalsDropdownNew(resetPayload);
    })();
  }, [task?.approvalAssignedType, task?.approvalAssignedId, task?.approvalAssignee, task?.approvalRelatedAssignedId]);

  useEffect(() => {
    setIsTaskLocked(isCurrentFlowLocked ? true : false);
  }, [isCurrentFlowLocked]);

  /**
   * handles approver action
   * @param {void}
   * @returns {void}
   * @author Himanshu Negi
   */
  const handleApproverAction = async () => {
    if (approverAction?.Status === "Approved" && isTaskLocked) await handleTaskKeyUpdate(false, "isTaskLocked", 1);
    setLoading(true);
    if (!approvalActionValidation(approvalActionValidationArgs)) {
      setLoading(false);
      return;
    }
    const { currentProjectIndex } = getCurrentActiveProject(flowElements);
    let approvalAssigneeName;
    const taskAssignmentId = flowElements[currentProjectIndex + number.ONE]?.taskAssignmentId;
    const taskAssignmentType = flowElements[currentProjectIndex + number.ONE]?.taskAssignmentType;
    if (taskAssignmentType === number.TWO) {
      approvalAssigneeName = defaultDetails?.assigneeList?.find((assignee) => assignee.value === taskAssignmentId)?.label;
    } else if (taskAssignmentType === number.THREE) {
      approvalAssigneeName = defaultDetails?.allProjectsList?.find((project) => project.ProjectId === taskAssignmentId)?.ProjectName;
    }
    const dynamicAssigneeDecider = flowElements[currentProjectIndex + number.ONE]?.variableId
    ? flowElements[currentProjectIndex + number.ONE]?.assignBasedOnCreator === number.ONE
      ? task.CreatedBy
      : user.id
    : null;
    const payload = {
      taskId: task?.taskId,
      assignedType: approvalAssignId?.AssignmentType ? approvalAssignId?.AssignmentType : task?.approvalAssignedType,
      assignedId: approvalAssignId?.value ? approvalAssignId?.value : task?.approvalAssignedId,
      assignedBy: user.id,
      approvalAssigneeName,
      currentProject: getApprovalCurrentProject(getApprovalCurrentProjectArgs),
      assignee:
        approvalRelatedAssignedId?.RelatedAssignmentType === number.TWO
          ? approvalRelatedAssignedId?.value
          : projectWorkflows[approvalRelatedAssignedId?.value]?.DefaultAssignee,
      approvalStatus: approverAction?.Status,
      comment: note,
      approvalRelatedAssignedType: approvalRelatedAssignedId?.RelatedAssignmentType ? approvalRelatedAssignedId?.RelatedAssignmentType : task?.approvalRelatedAssignedType,
      approvalRelatedAssignedId: approvalRelatedAssignedId?.value,
      taskHistoryId: task.taskHistoryId,
      isTaskLocked,
      isNextFlowInApproval: flowElements[currentProjectIndex + number.ONE]?.isApproval,
      nextDynamicVariableId: flowElements[currentProjectIndex + number.ONE]?.variableId || null,
      isRuleBasedTask: task?.IsRuleBasedTask,
      ...(!task?.IsRuleBasedTask && { dynamicAssigneeDecider }),
    };
    await dispatch(addTaskApprovalsHistory(payload, approverAction?.Status, taskMatchPath?.params));
    setLoading(false);
  };

  /**
   * Onchange handler for approval textarea
   * @param {Object} e
   * @returns void
   * @author Himanshu Negi
   */
  const onChangeHandler = (e) => {
    if (!e) return;
    const value = e.target.value;
    if (value.length <= number.ONE_HUNDRED_FIFTY) {
      setNote(value);
      e.target.style.height = "30px";
      e.target.style.height = `${e.target.scrollHeight}px`;
    }
  };

  /**
   * sets the value for isTaskLocked on click of icon
   * @author {Himanshu Negi}
   */
  const toggleTaskLock = useCallback(async () => {
    await hideTooltip();
    setIsTaskLocked(!isTaskLocked);
    await showTooltip();
  }, [isTaskLocked]);

  /**
   * show controlled tooltip on mouse over
   * @author Himanshu Negi
   */
  const showTooltip = useCallback(async () => {
    setOpen(true);
    await setTargetElement(wrapper.current);
  }, [wrapper?.current]);

  /**
   * hide controlled tooltip on mouse leave
   * @author Himanshu Negi
   */
  const hideTooltip = useCallback(async () => {
    setOpen(false);
    await setTargetElement(null);
  }, [wrapper?.current]);

  return (
    <React.Fragment>
      <div className='approver-container d-flex flex-column justify-content-center col-md-12'>
        <Tooltip position='bottom' anchorElement='target' parentTitle={true}>
          <div
            className={`${isApprover(task) || (isApprover(task) && approvalLogs?.length) ? "approval-approver-view" : "approval-non-approver-view justify-content-between"}`}>
            <div className='d-flex align-items-center justify-content-center mr-3'>
              <div className='mb-0'>
                <div className='approval-current-project'>
                  <span className='d-flex align-items-center justify-content-center'>
                    {assignedTypes.find((t) => t.key == approvalAssignId?.AssignmentType)?.icon}
                  </span>
                  <span className='text-truncate approval-current-name'>{approvalProjectName}</span>
                </div>
              </div>
            </div>
            {(isApprover(task) || (isApprover(task) && approvalLogs?.length)) && (
              <div className='approver-secondary-actions'>
                  <>
                    <label className='mt-2'>{label.NOTE}</label>
                    <button
                      id='approval-comment'
                      onClick={() => setShowNoteInput(!showNoteInput)}
                      disabled={task?.IsTaskClosed}
                      className='approval-originator-note-icon'
                      title={label.ADD_NOTE}>
                      {icon.NEW_COMMENT}
                    </button>
                    {task?.IsTaskLocked == number.ZERO && (
                      <div className='d-flex row flex-nowrap'>
                        <label className='ml-3 mt-2'>{label.LOCK}</label>
                        <button
                          id='approval-view-lock-task-btn'
                          ref={wrapper}
                          className={`approver-lock-icon ml-2 mr-4 ${isTaskLocked ? "text-blue" : "text-black"}`}
                          title={isTaskLocked ? tooltip.UNLOCK_TASK : tooltip.LOCK_TASK}
                          onClick={toggleTaskLock}
                          onMouseOver={showTooltip}
                          onMouseLeave={hideTooltip}
                          disabled={task?.IsTaskClosed}>
                          {isTaskLocked ? icon.APPROVAL_LOCK : icon.UNLOCK}
                        </button>
                      </div>
                    )}
                  </>
              </div>
            )}
            <div>
              <div className='approver-actions-logs d-flex align-items-end justify-content-between flex-grow-1'>
                <div className='approver-actions d-flex justify-content-end align-items-center'>
                  <div className="approval-assigne">
                    {!isApprover(task) || (!isApprover(task) && approvalLogs?.length) ? (
                      <div className='nonapprover-section'>
                        <span className='d-flex align-items-center justify-content-center text-blue'>{icon.APPROVAL_ICON}</span>
                        <span className="text-nowrap text-truncate">{approvalRelatedAssignedId?.label}</span>
                      </div>
                    ) : (
                      <DropDownList
                        id='approver-action-drop'
                        value={approverAction}
                        data={approverActionList}
                        itemRender={approverActionitemRender}
                        valueRender={approverActionvalueRender}
                        textField='Status'
                        dataItemKey='StatusId'
                        onChange={(obj) => {
                          setApproverAction(obj.value);
                        }}
                        defaultItem={{ Status: "Select", StatusId: number.ZERO }}
                        popupSettings={{ className: "approval-action-dropdown" }}
                        className={`${!approverAction ? "form-control-invalid" : ""}`}
                        disabled={(task?.IsTaskClosed)}
                      />
                    )}
                  </div>
                  {(isApprover(task) || (isApprover(task) && approvalLogs?.length)) && (
                    <button
                      id='approver-action-btn'
                      className='d-flex align-items-center justify-content-center cursor-pointer bg-transparent approval-action-tick border-0 p-0'
                      onClick={handleApproverAction}
                      disabled={loading || !approverAction}>
                      {icon.CHECKBOX}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
         {(isApprover(task) || (isApprover(task) && approvalLogs?.length)) ? <div className='information-panel d-flex panel-warning mt-2 w-100'>
            <span className='panel-icon d-flex align-items-center'>
              {icon.CIRCLE_ALERT}
            </span>
            <div className='panel-body p-2'>
              <div className='panel-heading cursor-default'>Approval</div>
              <div className='panel-message cursor-default'>Please review and select an action. Add notes or lock if required.</div>
            </div>
          </div> : <div className='information-panel d-flex panel-default mt-2 w-100'>
            <span className='panel-icon d-flex align-items-center'>
              {icon.CIRCLE_ALERT}
            </span>
            <div className='panel-body '>
              <div className='panel-heading cursor-default'>Pending Approval</div>
              <div className='panel-message cursor-default'>Awaiting review and approval</div>
            </div>
          </div>}
        <div className='approval-assign-form mb-2'>
            {showNoteInput && (
              <div className='approval-note-container'>
                <textarea
                  id='approver-note'
                  className='approval-note-area form-control'
                  value={note}
                  onChange={onChangeHandler}
                  placeholder={label.ADD_NOTE}
                  rows={number.ONE}
                  maxLength={number.ONE_HUNDRED_FIFTY}
                  autoFocus
                />
                <span className='approval-note-character-counter'>
                  {note.length}/{number.ONE_HUNDRED_FIFTY}
                </span>
              </div>
            )}
            <div className='approver-reassign-option'>
              {(approverAction?.StatusId === number.FIVE || approverAction?.StatusId === number.FOUR) && (
                <div>
                  <ApprovalAssignment
                    approverAction={approverAction}
                    approvalAssignType={approvalAssignType}
                    setApprovalAssignType={setApprovalAssignType}
                    approvalAssignId={approvalAssignId}
                    setApprovalAssignId={setApprovalAssignId}
                    approvalRelatedAssignedType={approvalRelatedAssignedType}
                    setApprovalRelatedAssignedType={setApprovalRelatedAssignedType}
                    approvalRelatedAssignedId={approvalRelatedAssignedId}
                    setApprovalRelatedAssignedId={setApprovalRelatedAssignedId}
                    relatedAssignedIdList={relatedAssignedIdList}
                    setRelatedAssignedIdList={setRelatedAssignedIdList}
                    approvalAssignee={approvalAssignee}
                    setApprovalAssignee={setApprovalAssignee}
                    approvalAssigneeName={approvalAssigneeName}
                    setApprovalAssigneeName={setApprovalAssigneeName}
                    approvalSelection={approvalSelection}
                    setApprovalSelection={setApprovalSelection}
                    disable={disable}
                    setDisable={setDisable}
                  />
                </div>
              )}
            </div>
          </div>
          {/* <OriginatorView /> */}
        </Tooltip>
      </div>
    </React.Fragment>
  );
};

export default ApproverView;